type RoyaltyPerfMeta = Record<string, string | number | boolean | null | undefined | Record<string, number>>

let navigationStartedAt: number | null = null

declare global {
  interface Window {
    __royaltyPerfEvents?: Array<RoyaltyPerfMeta & { event: string; ts: string; elapsedMs: number }>
  }
}

function isRoyaltyPerfEnabled(): boolean {
  if (import.meta.env.DEV) return true
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem('royaltyPerf') === '1'
  } catch {
    return false
  }
}

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

function getNavigationStartedAt(): number {
  if (navigationStartedAt != null) return navigationStartedAt
  if (typeof performance !== 'undefined') {
    const nav = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined
    navigationStartedAt = nav?.startTime ?? 0
    return navigationStartedAt
  }
  navigationStartedAt = Date.now()
  return navigationStartedAt
}

function payloadBytes(value: unknown): number | undefined {
  try {
    const json = JSON.stringify(value)
    if (typeof TextEncoder !== 'undefined') {
      return new TextEncoder().encode(json).length
    }
    return json.length
  } catch {
    return undefined
  }
}

export function summarizeRoyaltyPayload(value: unknown): RoyaltyPerfMeta {
  if (!isRoyaltyPerfEnabled()) return {}

  const bytes = payloadBytes(value)
  if (Array.isArray(value)) {
    return {
      rows: value.length,
      payloadBytes: bytes,
    }
  }

  if (!value || typeof value !== 'object') {
    return { payloadBytes: bytes }
  }

  const sectionRows: Record<string, number> = {}
  let rows = 0
  for (const [key, section] of Object.entries(value as Record<string, unknown>)) {
    if (Array.isArray(section)) {
      sectionRows[key] = section.length
      rows += section.length
    }
  }

  return {
    rows,
    payloadBytes: bytes,
    sectionRows,
  }
}

export function logRoyaltyPerf(event: string, meta: RoyaltyPerfMeta = {}) {
  if (!isRoyaltyPerfEnabled()) return
  const elapsedMs = Math.round(nowMs() - getNavigationStartedAt())
  const entry = {
    event,
    ts: new Date().toISOString(),
    elapsedMs,
    ...meta,
  }
  if (typeof window !== 'undefined') {
    window.__royaltyPerfEvents = [...(window.__royaltyPerfEvents ?? []), entry]
  }
  console.info('[royalty-perf]', entry)
}

export function resetRoyaltyNavigationClock() {
  navigationStartedAt = nowMs()
}

export async function measureRoyaltyAsync<T>(
  event: string,
  run: () => PromiseLike<T>,
  meta?: (result: T) => RoyaltyPerfMeta
): Promise<T> {
  if (!isRoyaltyPerfEnabled()) return run()

  const startedAt = nowMs()
  try {
    const result = await run()
    logRoyaltyPerf(event, {
      durationMs: Math.round(nowMs() - startedAt),
      ...(meta ? meta(result) : {}),
    })
    return result
  } catch (error) {
    logRoyaltyPerf(event, {
      durationMs: Math.round(nowMs() - startedAt),
      error: true,
    })
    throw error
  }
}

export function markRoyaltyPerf(event: string, meta: RoyaltyPerfMeta = {}) {
  logRoyaltyPerf(event, meta)
}

export function onRoyaltyProfilerRender(
  id: string,
  phase: 'mount' | 'update' | 'nested-update',
  actualDuration: number,
  baseDuration: number
) {
  logRoyaltyPerf(`render.${id}`, {
    phase,
    actualDurationMs: Math.round(actualDuration),
    baseDurationMs: Math.round(baseDuration),
  })
}
